import type { MutableState } from '../State';
import type { SyncActionAPI } from '../config/actions/createActions';
import { getJSTDateTime } from '../utils/time';
import { duration } from '../utils/duration';
import { Feature } from '../analytics';
import {
  JANKEN_CHALLENGE_EXPIRY_MS,
  JANKEN_MAX_DAILY_REWARDS,
} from './janken.ruleset';
import { canGrantJankenReward } from './janken.getters';
import {
  deriveJankenOutcome,
  rollJankenReward,
  jankenMapKey,
} from './janken.utils';
import { grantMatch2Rewards } from '../match2/match2.modifiers';
import { createReportableError } from '../utils/errors';
import { getEquippedPetSpecies } from '../pet/pet.getters';
import { getPlayerProfile } from '../profile/profile.getters';

type JankenState = NonNullable<MutableState['janken']>;

/**
 * Lazily initialize janken state for first-time users.
 * Safe to call multiple times — returns the existing state if already initialized.
 *
 * @example
 * const janken = ensureJankenState(state);
 * janken.dailyRewards += 1;
 */
export function ensureJankenState(state: MutableState): JankenState {
  if (!state.janken) {
    state.janken = {
      dailyRewards: 0,
      resolvedFriends: {},
      resolvedChallenges: {},
      pendingResults: {},
    };
  }
  return state.janken;
}

/**
 * Mark the janken tutorial as seen by recording the current timestamp.
 */
export function setJankenTutorialSeen(state: MutableState, now: number): void {
  const janken = ensureJankenState(state);
  janken.tutorialSeenAt = now;
}

/**
 * Reset daily tracking if the JST calendar day has changed.
 * No-op if still on the same JST day.
 *
 * @example
 * resetJankenDaily(state, api.date.now());
 * // dailyRewards is now 0 if the JST day rolled over
 */
export function resetJankenDaily(state: MutableState, now: number): void {
  const janken = ensureJankenState(state);
  const lastStamp = janken.dailyStamp;

  if (
    lastStamp &&
    getJSTDateTime(lastStamp).hasSame(getJSTDateTime(now), 'day')
  ) {
    return;
  }

  janken.dailyStamp = now;
  janken.dailyRewards = 0;
}

/**
 * Record that a reward was granted: increment daily total and mark friend as resolved.
 *
 * @example
 * recordJankenReward(state, friendId, now);
 * // state.janken.dailyRewards is now +1
 * // state.janken.resolvedFriends[friendId] is now set
 */
export function recordJankenReward(
  state: MutableState,
  friendId: string,
  now: number,
): void {
  // Ensure dailyStamp is set so canGrantJankenReward sees the real count.
  resetJankenDaily(state, now);
  const janken = ensureJankenState(state);
  if (janken.dailyRewards >= JANKEN_MAX_DAILY_REWARDS) {
    throw new Error(
      `janken: daily reward cap (${JANKEN_MAX_DAILY_REWARDS}) already reached`,
    );
  }
  janken.dailyRewards += 1;
  janken.resolvedFriends[friendId] = now;
}

/**
 * Mark a challenge as resolved for the replay guard.
 * Prevents duplicate OA notifications when a user re-taps the same challenge link.
 *
 * @example
 * markChallengeResolved(state, 'jnk_1708300000000_482917365', senderId, now);
 */
export function markChallengeResolved(
  state: MutableState,
  challengeId: string,
  senderId: string,
  now: number,
): void {
  const janken = ensureJankenState(state);
  janken.resolvedChallenges[jankenMapKey(challengeId, senderId)] = now;
}

/**
 * Create a sender-pending result entry.
 * Key format: `${challengeId}_${receiverId}`.
 *
 * @example
 * createJankenPendingResult(state, {
 *   challengeId: 'jnk_1708300000000_482917365',
 *   receiverId: 'U9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b',
 *   createdAt: Date.now(),
 * });
 */
export function createJankenPendingResult(
  state: MutableState,
  params: {
    challengeId: string;
    receiverId: string;
    createdAt: number;
    opponentSpecies?: string;
    opponentDisplayName?: string;
  },
): void {
  const janken = ensureJankenState(state);
  janken.pendingResults[jankenMapKey(params.challengeId, params.receiverId)] =
    params;
}

/**
 * Delete a pending result entry after claiming.
 *
 * @example
 * deleteJankenPendingResult(state, 'jnk_1708300000000_482917365_U9e8d7c6b5a4f3e2d1c0b9a8f7e6d5c4b');
 */
export function deleteJankenPendingResult(
  state: MutableState,
  key: string,
): void {
  const janken = ensureJankenState(state);
  delete janken.pendingResults[key];
}

/**
 * Aggressive cleanup — called from onLogin. Keeps state as small as possible.
 *
 * Removes:
 * - Pending results older than 25h (sender didn't claim in time)
 * - Resolved friends from previous JST calendar days
 * - Resolved challenges older than 25h
 * - Resets daily counters if the JST day rolled over
 *
 * Uses 25h (24h + 1h grace) to prevent premature cleanup near the expiry boundary.
 *
 * @example
 * jankenCleanupExpired(state, api.date.now());
 */
export function jankenCleanupExpired(state: MutableState, now: number): void {
  if (!state.janken) return;

  const expiry = JANKEN_CHALLENGE_EXPIRY_MS + duration({ hours: 1 });
  const todayJST = getJSTDateTime(now);

  // Delete pending results older than 25h (sender didn't claim in time)
  for (const [key, entry] of Object.entries(state.janken.pendingResults)) {
    if (now - entry.createdAt > expiry) {
      delete state.janken.pendingResults[key];
    }
  }

  // Delete resolvedFriends entries from previous JST calendar days
  for (const [friendId, stamp] of Object.entries(
    state.janken.resolvedFriends,
  )) {
    if (!getJSTDateTime(stamp).hasSame(todayJST, 'day')) {
      delete state.janken.resolvedFriends[friendId];
    }
  }

  // Delete resolvedChallenges entries older than 25h
  for (const [key, stamp] of Object.entries(state.janken.resolvedChallenges)) {
    if (now - stamp > expiry) {
      delete state.janken.resolvedChallenges[key];
    }
  }

  // Daily reset
  resetJankenDaily(state, now);
}

/**
 * Shared receiver resolution core: daily reset → derive → reward → notify sender.
 * Called by `resolveJankenChallenge` for both immediate and deferred receiver resolution.
 */
export function resolveReceiverOutcome(
  state: MutableState,
  challengeId: string,
  senderId: string,
  receiverId: string,
  now: number,
  api: SyncActionAPI,
): void {
  resetJankenDaily(state, now);

  const { result, senderMove, receiverMove } = deriveJankenOutcome(
    challengeId,
    senderId,
    receiverId,
  );

  if (result.receiver === 'win' && canGrantJankenReward(state, senderId, now)) {
    const reward = rollJankenReward(challengeId, senderId, receiverId);
    grantMatch2Rewards(
      state,
      [reward],
      { feature: Feature.JANKEN._, $subFeature: Feature.JANKEN.LUCKY_ITEM },
      api,
    );
    recordJankenReward(state, senderId, now);
  }

  const receiverName = getPlayerProfile(
    state,
    state.publicProfile?.name,
    undefined,
  ).name;
  const receiverSpecies = getEquippedPetSpecies(state) ?? 'starball';

  try {
    // Do not send to fake users
    if (senderId.startsWith('U')) {
      api.postMessage.jankenChallengeResolved(senderId, {
        challengeId,
        opponentSpecies: receiverSpecies,
        opponentDisplayName: receiverName,
      });
    }

    api.scheduledActions.schedule.jankenNotifyResult({
      args: {
        challengeId,
        receiverId,
        outcome: result.sender,
        opponentName: receiverName,
        senderMove,
        receiverMove,
        opponentSpecies: receiverSpecies,
      },
      delayInMS: 0,
      targetUserId: senderId,
      notificationId: `jankenNotifyResult_${challengeId}_${receiverId}`,
    });
  } catch (error) {
    api.reportError(createReportableError('error', error as Error));
  }
}
